import * as React from 'react';
import { Button } from '@/components/forms/ui/button';

// Two-tier consequence model (see docs/plans/2026-08-20-assist-me-commit-card-taxonomy.md):
// 'staged' = the write stays in the proposal staging area; 'live' = it leaves it (Bravo execution
// or any DSP write, even paused structure). The tier colors how far the write reaches; the frame
// itself marks that a deterministic human decision is pending at all.
export type CommitTier = 'staged' | 'live';

export type CommitOutcome = {
  status: 'applied' | 'cancelled' | 'failed';
  /** Receipt line, e.g. "3 line items updated". Optional for cancelled (default text). */
  summary?: string;
};

// Provided by AiChatModal. Confirm registers the tool call here BEFORE the write starts, so the
// modal's soft-cancel (which resolves pending commit calls as cancelled when the user sends a
// message) never false-cancels a write that is already executing. Entries are permanent for the
// session: a confirmed call always ends resolved by the card itself, on success, failure, or cancel.
export const CommitInFlightContext = React.createContext<{
  markInFlight: (toolCallId: string) => void;
} | null>(null);

/**
 * Map a resolved commit-tool part to the receipt outcome a card should show when it did not
 * resolve the call itself (a prior session, or the modal's soft-cancel). Returns null while the
 * call is unresolved.
 */
export function externalCommitOutcome(part: {
  state: string;
  output?: unknown;
}): CommitOutcome | null {
  if (part.state === 'output-error') return { status: 'failed' };
  if (part.state !== 'output-available') return null;
  const status = (part.output as { status?: string } | null | undefined)?.status;
  if (status === 'cancelled') return { status: 'cancelled' };
  if (status === 'failed') return { status: 'failed' };
  return { status: 'applied' };
}

export type CommitCardFrameProps = {
  tier: CommitTier;
  title: string;
  /** Tier badge text; defaults per tier. Override for DSP writes (e.g. "Writes to Beeswax"). */
  badgeLabel?: string;
  /** One-line consequence statement, always visible while the decision is pending. */
  consequence: string;
  confirmLabel: string;
  confirmDisabled?: boolean;
  isPending: boolean;
  /** Terminal outcome reached in this session; null while the decision is pending. */
  outcome: CommitOutcome | null;
  /** Resolved outside this card (tool part already carries output) — receipt, no buttons.
   * Derive via externalCommitOutcome so a soft-cancelled card shows the cancelled receipt. */
  externalOutcome?: CommitOutcome | null;
  /** Registers Confirm clicks with the in-flight registry (see CommitInFlightContext). */
  toolCallId?: string;
  /** Non-terminal inline error: the write failed but the card stays actionable for a retry. */
  errorMsg?: string | null;
  /** The card content was updated after it resolved (e.g. a re-propose after a soft-cancel) —
   * open the receipt and flag that the new version needs review. */
  receiptUpdated?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void | Promise<void>;
  children: React.ReactNode;
};

/**
 * Shared frame for every assist-me commit card — the surfaces whose Confirm button
 * deterministically writes to Bravo or a DSP (no LLM in that path). Owns the tier accent + badge,
 * the consequence line, the Cancel/Confirm footer, and the resolved "receipt" collapse, so every
 * write moment across the line-item and media-plan flows reads the same way.
 */
export function CommitCardFrame({
  tier,
  title,
  badgeLabel,
  consequence,
  confirmLabel,
  confirmDisabled,
  isPending,
  outcome,
  externalOutcome,
  toolCallId,
  errorMsg,
  receiptUpdated,
  onConfirm,
  onCancel,
  children
}: CommitCardFrameProps): JSX.Element {
  const inFlight = React.useContext(CommitInFlightContext);
  // The card's own outcome wins — it carries the specific summary; the external one is generic.
  const finalOutcome = outcome ?? externalOutcome ?? null;
  if (finalOutcome) {
    return (
      <Receipt outcome={finalOutcome} title={title} updated={receiptUpdated ?? false}>
        {children}
      </Receipt>
    );
  }

  const handleConfirm = () => {
    // Register BEFORE the write starts — from here the card owns the call's resolution.
    if (toolCallId) inFlight?.markInFlight(toolCallId);
    void onConfirm();
  };

  const live = tier === 'live';
  return (
    <div
      className={`rounded-xl border-2 bg-white p-4 shadow-md ${
        live ? 'border-amber-400' : 'border-blue-400'
      }`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-gray-800">{title}</div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            live ? 'bg-amber-100 text-amber-800' : 'bg-blue-50 text-blue-700'
          }`}>
          {badgeLabel ?? (live ? 'Affects live delivery' : 'Updates proposal')}
        </span>
      </div>

      {children}

      <div className="mt-4 border-t pt-3">
        {errorMsg ? <div className="mb-2 text-xs text-red-600">{errorMsg}</div> : null}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={`text-xs ${live ? 'text-amber-700' : 'text-blue-700'}`}>
            {consequence}
          </span>
          <div className="flex shrink-0 items-center gap-2">
            <Button onClick={onCancel} disabled={isPending} size="sm" variant="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isPending || confirmDisabled}
              size="sm"
              className={live ? 'bg-amber-600 text-white hover:bg-amber-600/90' : undefined}
              variant={live ? undefined : 'blue'}>
              {isPending ? 'Applying...' : confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Collapsed one-line receipt for a resolved commit, expandable to review the card body. When the
// content was updated after resolution (`updated`), it opens by default and flags the new version.
function Receipt({
  outcome,
  title,
  updated,
  children
}: {
  outcome: CommitOutcome;
  title: string;
  updated: boolean;
  children: React.ReactNode;
}): JSX.Element {
  const { icon, text, className } = receiptLine(outcome);
  return (
    <details className="rounded-xl border border-gray-200 bg-white shadow-sm" open={updated}>
      <summary className="flex cursor-pointer select-none items-center gap-2 px-4 py-2.5 text-sm">
        <span className={`font-semibold ${className}`} aria-hidden>
          {icon}
        </span>
        <span className={`font-medium ${className}`}>{text}</span>
        {updated ? (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
            Updated — review
          </span>
        ) : null}
        <span className="ml-auto text-xs text-gray-400">{title}</span>
      </summary>
      <div className="border-t p-4">
        {updated ? (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            <p className="font-semibold">This plan changed after the card was closed</p>
            <p className="mt-1">
              The content below is the newest version, but this card can no longer create it. Ask in
              chat to create the plan and a fresh confirmation card will appear.
            </p>
          </div>
        ) : null}
        {children}
      </div>
    </details>
  );
}

function receiptLine(outcome: CommitOutcome): {
  icon: string;
  text: string;
  className: string;
} {
  switch (outcome.status) {
    case 'applied':
      return { icon: '✓', text: outcome.summary ?? 'Applied', className: 'text-green-600' };
    case 'cancelled':
      return {
        icon: '✕',
        text: outcome.summary ?? 'Cancelled — no changes made',
        className: 'text-gray-500'
      };
    case 'failed':
      return {
        icon: '⚠',
        text: outcome.summary ?? 'Failed — no changes made',
        className: 'text-red-600'
      };
  }
}
